import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Divider,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SecurityIcon from "@mui/icons-material/Security";
import { useNotifications } from "@toolpad/core/useNotifications";
import { useAuth } from "src/context/auth";
import { getRoutes } from "src/utilities/routes";
import { useMutation } from "@tanstack/react-query";
import {
  IMenuPermission,
  IUserPermission,
  ISavePermissionsRequest,
  IPermissionsResponse,
} from "src/interface/IPermission";

interface PermissionDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  onSuccess?: () => void;
}

// تعریف ساختار منوها و دسترسی‌ها
interface PermissionDialogState {
  menus: IMenuPermission[];
}

const PermissionDialog: React.FC<PermissionDialogProps> = ({
  open,
  onClose,
  userId,
  userName,
  onSuccess,
}) => {
  const { user } = useAuth();
  const notifications = useNotifications();
  const Routes = getRoutes();

  // تعریف منوهای سیستم
  const systemMenus: IMenuPermission[] = [
    {
      id: "tasks",
      name: "کارها",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "my-tasks",
      name: "کارهای من",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "employees",
      name: "کارمندان",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "customers",
      name: "مشتریان",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "agreements",
      name: "قراردادها",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "services",
      name: "خدمات",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "transactions",
      name: "تراکنش‌ها",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "followups",
      name: "پیگیری‌ها",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "reports",
      name: "گزارشات",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "settings",
      name: "تنظیمات",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "base-definitions",
      name: "تعاریف پایه",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "duties",
      name: "وظایف",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
    {
      id: "sales-funnel",
      name: "قیف فروش",
      permissions: { view: false, create: false, edit: false, delete: false },
    },
  ];

  const [permissions, setPermissions] =
    useState<IMenuPermission[]>(systemMenus);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  // دریافت دسترسی‌های فعلی کاربر
  const { mutateAsync: getUserPermissions, isPending: isLoadingPermissions } =
    useMutation({
      mutationFn: async () => {
        const response = await fetch(
          `${Routes.UserPermissionsRoutes}${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user?.token}`,
              Accept: "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return await response.json();
      },
      onSuccess: (data) => {
        if (data && data.permissions) {
          // به‌روزرسانی دسترسی‌ها بر اساس داده‌های دریافتی
          const updatedPermissions = permissions.map((menu) => {
            const userPermission = data.permissions.find(
              (p: any) => p.menuId === menu.id
            );
            if (userPermission) {
              return {
                ...menu,
                permissions: {
                  view: userPermission.view || false,
                  create: userPermission.create || false,
                  edit: userPermission.edit || false,
                  delete: userPermission.delete || false,
                },
              };
            }
            return menu;
          });
          setPermissions(updatedPermissions);
        }
      },
      onError: (error) => {
        console.error("خطا در دریافت دسترسی‌ها:", error);
        notifications.show("خطا در دریافت دسترسی‌های کاربر", {
          severity: "error",
          autoHideDuration: 3000,
        });
      },
    });

  // ذخیره دسترسی‌های کاربر
  const { mutateAsync: saveUserPermissions, isPending: isSaving } = useMutation(
    {
      mutationFn: async (permissionsData: IMenuPermission[]) => {
        const response = await fetch(
          `${Routes.UserPermissionsRoutes}${userId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${user?.token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              userId,
              permissions: permissionsData.map((menu) => ({
                menuId: menu.id,
                ...menu.permissions,
              })),
            }),
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return await response.json();
      },
      onSuccess: () => {
        notifications.show("دسترسی‌های کاربر با موفقیت ذخیره شد", {
          severity: "success",
          autoHideDuration: 3000,
        });
        onSuccess?.();
        onClose();
      },
      onError: (error) => {
        console.error("خطا در ذخیره دسترسی‌ها:", error);
        notifications.show("خطا در ذخیره دسترسی‌های کاربر", {
          severity: "error",
          autoHideDuration: 3000,
        });
      },
    }
  );

  // بارگذاری دسترسی‌ها هنگام باز شدن دیالوگ
  useEffect(() => {
    if (open && userId) {
      getUserPermissions();
    }
  }, [open, userId]);

  // تغییر وضعیت دسترسی
  const handlePermissionChange = (
    menuId: string,
    permissionType: keyof IMenuPermission["permissions"],
    checked: boolean
  ) => {
    setPermissions((prev) =>
      prev.map((menu) =>
        menu.id === menuId
          ? {
              ...menu,
              permissions: {
                ...menu.permissions,
                [permissionType]: checked,
              },
            }
          : menu
      )
    );
  };

  // تغییر وضعیت نمایش/مخفی کردن منو
  const handleMenuToggle = (menuId: string) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  // انتخاب همه دسترسی‌ها برای یک منو
  const handleSelectAllPermissions = (menuId: string, checked: boolean) => {
    setPermissions((prev) =>
      prev.map((menu) =>
        menu.id === menuId
          ? {
              ...menu,
              permissions: {
                view: checked,
                create: checked,
                edit: checked,
                delete: checked,
              },
            }
          : menu
      )
    );
  };

  // ذخیره دسترسی‌ها
  const handleSave = () => {
    saveUserPermissions(permissions);
  };

  // بستن دیالوگ
  const handleClose = () => {
    setPermissions(systemMenus);
    setExpandedMenus([]);
    onClose();
  };

  // بررسی اینکه آیا همه دسترسی‌های یک منو انتخاب شده‌اند
  const isAllPermissionsSelected = (menu: IMenuPermission) => {
    return Object.values(menu.permissions).every((permission) => permission);
  };

  // بررسی اینکه آیا هیچ دسترسی‌ای انتخاب نشده
  const isNoPermissionSelected = (menu: IMenuPermission) => {
    return Object.values(menu.permissions).every((permission) => !permission);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "70vh",
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <SecurityIcon color="primary" />
          <Typography variant="h6">مدیریت سطح دسترسی - {userName}</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            دسترسی‌های مورد نظر برای کاربر را انتخاب کنید. هر منو شامل چهار سطح
            دسترسی است:
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Chip label="نمایش" size="small" color="primary" />
            <Chip label="ایجاد" size="small" color="success" />
            <Chip label="ویرایش" size="small" color="warning" />
            <Chip label="حذف" size="small" color="error" />
          </Stack>
        </Alert>

        <Box sx={{ maxHeight: "50vh", overflow: "auto" }}>
          {permissions.map((menu) => (
            <Accordion
              key={menu.id}
              expanded={expandedMenus.includes(menu.id)}
              onChange={() => handleMenuToggle(menu.id)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  sx={{ pr: 2 }}
                >
                  <Typography variant="subtitle1" fontWeight="medium">
                    {menu.name}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {Object.entries(menu.permissions).map(([key, value]) => (
                      <Chip
                        key={key}
                        label={
                          key === "view"
                            ? "نمایش"
                            : key === "create"
                              ? "ایجاد"
                              : key === "edit"
                                ? "ویرایش"
                                : "حذف"
                        }
                        size="small"
                        color={
                          value
                            ? key === "view"
                              ? "primary"
                              : key === "create"
                                ? "success"
                                : key === "edit"
                                  ? "warning"
                                  : "error"
                            : "default"
                        }
                        variant={value ? "filled" : "outlined"}
                      />
                    ))}
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pl: 2 }}>
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isAllPermissionsSelected(menu)}
                          indeterminate={
                            !isAllPermissionsSelected(menu) &&
                            !isNoPermissionSelected(menu)
                          }
                          onChange={(e) =>
                            handleSelectAllPermissions(
                              menu.id,
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="انتخاب همه"
                    />
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    {Object.entries(menu.permissions).map(([key, value]) => (
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            checked={value}
                            onChange={(e) =>
                              handlePermissionChange(
                                menu.id,
                                key as keyof IMenuPermission["permissions"],
                                e.target.checked
                              )
                            }
                            color={
                              key === "view"
                                ? "primary"
                                : key === "create"
                                  ? "success"
                                  : key === "edit"
                                    ? "warning"
                                    : "error"
                            }
                          />
                        }
                        label={
                          key === "view"
                            ? "نمایش"
                            : key === "create"
                              ? "ایجاد"
                              : key === "edit"
                                ? "ویرایش"
                                : "حذف"
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isSaving}>
          انصراف
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || isLoadingPermissions}
        >
          {isSaving ? "در حال ذخیره..." : "ذخیره دسترسی‌ها"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionDialog;
